<template>
    <div class="ask-pendo-thread">
        <!-- eslint-disable vue/no-unused-refs -->
        <div
            ref="threadBody"
            class="ask-pendo-thread__body"
            :style="{ '--body-height': `${bodyHeight}px` }">
            <div
                ref="threadMessages"
                class="ask-pendo-thread__messages">
                <ask-pendo-message
                    v-for="(message, index) in filteredMessages"
                    :ref="`item-${message.id}`"
                    :key="message.id"
                    :show-timestamp="showMessageTimestamps"
                    :is-guide-chat="isGuideChat"
                    :message="message"
                    @send-message="handleUpdate">
                    <template #reasoning>
                        <slot
                            name="reasoning"
                            :message="message" />
                    </template>
                    <template #artifact>
                        <slot
                            name="artifact"
                            :message="message" />
                    </template>
                    <template
                        v-if="showFeedbackRow(message, index)"
                        #feedback>
                        <div class="ask-pendo-thread__feedback-row">
                            <copy-message
                                v-if="!isGuideChat"
                                v-pendo-tooltip="{ content: $t('askPendo.Copy response'), placement: 'bottom' }"
                                class="ask-pendo-thread__copy-to-clipboard"
                                :message="message"
                                @copied="onCopied(index)" />
                            <thumbs-up-thumbs-down
                                @thumbs-up="handleFeedbackTrackEvent(THUMBS_UP_CLICK, index)"
                                @thumbs-down="handleFeedbackTrackEvent(THUMBS_DOWN_CLICK, index)"
                                @feedback-submitted="
                                    handleFeedbackTrackEvent(THUMBS_DOWN_FEEDBACK, index, {
                                        feedback: $event,
                                    })
                                " />
                            <undo-llm-change
                                v-if="showLLMUndoButton(message)"
                                v-pendo-tooltip="{ content: undoEditButtonTooltip, placement: 'bottom' }"
                                @undo="handleLlmUndo(index)" />
                            <pendo-icon-button
                                v-if="showRetryButton(message, index)"
                                v-pendo-tooltip="{ content: $t('askPendo.Retry'), placement: 'bottom' }"
                                icon="refresh-cw"
                                icon-size="16"
                                @click="handleRetry(message)" />
                        </div>
                        <div
                            v-if="copiedIndex === index"
                            class="ask-pendo-thread__copied-message">
                            {{ $t('askPendo.Response copied') }}
                        </div>
                    </template>
                </ask-pendo-message>
                <div
                    v-if="error"
                    class="ask-pendo-thread__error">
                    <ask-pendo-message
                        :is-guide-chat="isGuideChat"
                        :message="{
                            type: 'error',
                            content: error,
                        }" />
                </div>
                <slot name="loading" />
            </div>
        </div>
        <pendo-button
            v-if="!isScrolledToBottom"
            class="ask-pendo-thread__scroll-to-bottom"
            type="secondary"
            size="small"
            @click="scrollToBottom">
            <template #default>
                <pendo-icon
                    type="arrow-down"
                    size="16" />
            </template>
        </pendo-button>

        <div class="ask-pendo-thread__footer">
            <ask-pendo-workflow
                v-if="!isEmpty(plan) && plan.id !== dismissedPlanId"
                :todos="plan.todos"
                @dismiss="handleWorkflowDismiss" />
            <ask-pendo-next-actions
                v-if="showNextActions"
                v-show="!pending && !typing"
                :conversation-id="conversationId" />
            <ask-pendo-input
                :placeholder="inputPlaceholder"
                :is-guide-chat="isGuideChat"
                :custom-button-icon="buttonIcon"
                mode="conversation"
                :loading="pending"
                :typing="typing"
                @update="handleUpdate($event)"
                @stop="handleStop" />
        </div>
    </div>
</template>

<script>
import AskPendoInput from '@/ask-pendo/components/ask-pendo-input.vue';
import AskPendoMessage from '@/ask-pendo/components/ask-pendo-message.vue';
import AskPendoNextActions from '@/ask-pendo/components/ask-pendo-next-actions.vue';
import AskPendoWorkflow from '@/ask-pendo/components/ask-pendo-workflow.vue';
import { PendoButton, PendoIcon, PendoTooltip, PendoIconButton } from '@pendo/components';
import ThumbsUpThumbsDown from '@/ask-pendo/components/thumbs-up-thumbs-down.vue';
import CopyMessage from '@/ask-pendo/components/copy-message.vue';
import UndoLlmChange from '@/ask-pendo/components/undo-llm-change.vue';
import { templateRef, useElementSize, useScroll } from '@vueuse/core';
import { isCustomInstructionsMessage, isNextActionsProgress, isToolCall, isToolResponse } from '@/ask-pendo/utils/message';
import { buildPayload, trackAgentEvent } from '@/ask-pendo/utils/agent-analytics';
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';

const LLM_UNDO = 'llm_undo';
const RETRY_MESSAGE = 'retry_message';
const THUMBS_UP_CLICK = 'thumbs_up_click';
const THUMBS_DOWN_CLICK = 'thumbs_down_click';
const THUMBS_DOWN_FEEDBACK = 'thumbs_down_feedback_submitted';

export default {
    name: 'AskPendoThread',
    components: {
        AskPendoInput,
        AskPendoMessage,
        UndoLlmChange,
        AskPendoNextActions,
        CopyMessage,
        PendoButton,
        PendoIcon,
        PendoIconButton,
        ThumbsUpThumbsDown,
        AskPendoWorkflow,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        agentId: {
            type: String,
            default: null,
        },
        // conversationId is still needed for parent component communication, but no longer used directly in this component

        conversationId: {
            type: String,
            required: true,
        },
        customButtonIcon: {
            type: String,
            default: 'arrow-right',
        },
        error: {
            type: String,
            default: null,
        },
        shouldShowLlmUndo: {
            type: Boolean,
            default: false,
        },
        inputPlaceholder: {
            type: String,
            default: '',
        },
        isGuideChat: {
            type: Boolean,
            default: false,
        },
        messages: {
            type: Array,
            required: true,
        },
        pending: {
            type: Boolean,
            default: false,
        },
        typing: {
            type: Boolean,
            default: false,
        },
        showMessageTimestamps: {
            type: Boolean,
            default: false,
        },
        showNextActions: {
            type: Boolean,
            default: false,
        },
        plan: {
            type: Object,
            default: null,
        },
    },
    emits: ['update', 'stop', 'undo-llm-change', 'retry'],
    setup () {
        const threadBody = templateRef('threadBody');
        const threadMessages = templateRef('threadMessages');

        const { height: bodyHeight } = useElementSize(threadBody);
        const { height: messagesHeight } = useElementSize(threadMessages);

        const { arrivedState } = useScroll(threadBody, {
            offset: { bottom: 50 },
        });

        return {
            arrivedState,
            bodyHeight,
            messagesHeight,
        };
    },
    data () {
        return {
            copiedIndex: null,
            copyTimeout: null,
            dismissedPlanId: null,
            THUMBS_UP_CLICK,
            THUMBS_DOWN_CLICK,
            THUMBS_DOWN_FEEDBACK,
        };
    },
    computed: {
        ...mapGetters('askPendo', ['getEmployeeConfig']),
        buttonIcon () {
            if (this.pending) {
                return '';
            }

            return this.customButtonIcon;
        },
        filteredMessages () {
            return this.messages.filter((message) => {

                return !(isToolCall(message) || isToolResponse(message) || isNextActionsProgress(message) || isCustomInstructionsMessage(message));
            });
        },
        isScrolledToBottom () {
            // If content isn't scrollable, we're always "at the bottom"
            const isContentScrollable = this.messagesHeight > this.bodyHeight;
            if (!isContentScrollable) {
                return true;
            }

            return this.arrivedState?.bottom ?? true;
        },
    },
    watch: {
        messages: {
            handler (newMessages, oldMessages) {
                if (!oldMessages || newMessages.length > oldMessages.length) {
                    this.scrollToBottom();
                }
            },
            deep: true,
        },
        messagesHeight () {
            if (this.arrivedState?.bottom) {
                this.scrollToBottom();
            }
        },
        pending (newVal) {
            if (newVal && this.arrivedState?.bottom) {
                this.scrollToBottom();
            }
        },
        typing (isTyping) {
            if (!isTyping && this.arrivedState?.bottom) {
                this.scrollToBottom();
            }
        },
    },
    mounted () {
        this.scrollToBottom();
        this.undoEditButtonTooltip = this.$t('askPendoThread.undoEditButtonTooltip');
    },
    methods: {
        handleWorkflowDismiss () {
            this.dismissedPlanId = this.plan?.id;
        },
        handleCopyTrackEvent (messageIndex) {
            const question = this.filteredMessages[messageIndex - 1]?.content;
            const response = this.filteredMessages[messageIndex]?.content;
            const eventData = {
                question,
                response,
                location: 'analytics agent',
            };
            window.pendo.track('copy_click', eventData);
        },
        handleFeedbackTrackEvent (eventName, messageIndex, eventData = {}) {
            // Use optional chaining to safely access content even if message doesn't exist
            // This allows tracking analytics for insights templates where there's no previous user message
            eventData.question = this.filteredMessages[messageIndex - 1]?.content;
            eventData.response = this.filteredMessages[messageIndex]?.content;
            eventData.location = 'analytics agent';
            window.pendo.track(eventName, eventData);

            // track Agent reaction analytics when available
            this.trackAgentReaction(eventName, messageIndex);
        },
        handleRetry (message) {
            // Locate the assistant message in the raw array
            const assistantIndex = this.messages.findIndex((msg) => msg?.id === message?.id);
            if (assistantIndex <= 0) return;

            // Walk back to find the previous user message
            for (let i = assistantIndex - 1; i >= 0; i--) {
                const prev = this.messages[i];
                if (prev?.type === 'user') {
                    const messageIndex = this.filteredMessages.indexOf(message);
                    this.trackAgentReaction(RETRY_MESSAGE, messageIndex);
                    const payload = {
                        text: prev?.content,
                        files: prev?.metadata?.files || [],
                    };
                    this.$emit('retry', payload);

                    return;
                }
            }
        },
        handleStop () {
            this.$emit('stop');
        },
        handleUpdate (text) {
            this.$emit('update', text);
        },
        handleLlmUndo (messageIndex) {
            this.trackAgentReaction(LLM_UNDO, messageIndex);
            this.$emit('undo-llm-change');
            setTimeout(() => {
                this.scrollToBottom();
            }, 300);
        },
        onCopied (index) {
            this.handleCopyTrackEvent(index);
            this.copiedIndex = index;
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.copyTimeout = setTimeout(() => {
                this.copiedIndex = null;
                this.copyTimeout = null;
            }, 5000);
        },
        scrollToBottom () {
            this.$nextTick(() => {
                if (this.$refs.threadBody) {
                    this.$refs.threadBody.scrollTop = this.$refs.threadBody.scrollHeight;
                }
            });
        },
        // eslint-disable-next-line internal-rules/no-unused-properties
        scrollToMessage (messageId) {
            const targetElement = this.$refs[`item-${messageId}`]?.[0]?.$el;
            if (!targetElement) return;

            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        },
        showFeedbackRow (message, index) {
            if (message.type !== 'assistant') return false;

            const numMessages = this.filteredMessages.length;
            if (index < numMessages - 1) return true;

            return !(this.pending || this.typing);
        },
        showLLMUndoButton (message) {
            const isLastMessage = this.filteredMessages.indexOf(message) === this.filteredMessages.length - 1;

            return this.shouldShowLlmUndo && isLastMessage;
        },
        showRetryButton (message, index) {
            // Only for assistant messages
            if (message.type !== 'assistant') return false;

            const isLastAssistant = index === this.filteredMessages.length - 1;
            const canRetryNow = !this.shouldShowLlmUndo && !this.pending && !this.typing;

            // Check if there's a previous user message to retry
            const assistantIndex = this.messages.findIndex((msg) => msg?.id === message?.id);
            const hasPreviousUserMessage =
                assistantIndex > 0 && this.messages.slice(0, assistantIndex).some((msg) => msg?.type === 'user');

            return isLastAssistant && canRetryNow && hasPreviousUserMessage;
        },
        trackAgentReaction (eventName, messageIndex) {
            if (!this.agentId) {
                return;
            }
            const assistantMsg = this.filteredMessages[messageIndex];
            if (!assistantMsg || assistantMsg.type !== 'assistant') return;

            let sentiment = null;
            switch (eventName) {
                case THUMBS_UP_CLICK:
                    sentiment = 'positive';
                    break;
                case THUMBS_DOWN_CLICK:
                case THUMBS_DOWN_FEEDBACK:
                    sentiment = 'negative';
                    break;
                case LLM_UNDO:
                    sentiment = 'undo';
                    break;
                case RETRY_MESSAGE:
                    sentiment = 'retry';
                    break;
                default:
                    return;
            }

            try {
                const payload = buildPayload({
                    agentId: this.agentId,
                    conversationId: this.conversationId,
                    messageId: assistantMsg.id,
                    content: sentiment,
                    modelUsed: this.getEmployeeConfig?.model || null,
                    suggestedPrompt: false,
                    toolsUsed: [],
                    fileUploaded: false,
                });
                trackAgentEvent('user_reaction', payload);
            } catch {
                // no-op
            }
        },
        isEmpty,
    },
};
</script>

<style scoped lang="less">
.ask-pendo-thread {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    &__body {
        flex: 1;
        width: 100%;
        min-height: 0;
        overflow-y: auto;
        scroll-behavior: smooth;

        // allow scrolling content to be visible behind rounded borders of ask-pendo-input
        margin-bottom: -8px;
    }

    &__messages {
        display: flex;
        flex-direction: column;
        gap: 40px;
        position: relative;

        max-width: 848px;
        margin: 0 auto 12px;
        padding: 24px 16px 0 16px;
        white-space: pre-line;

        .ask-pendo-message:last-of-type {
            min-height: calc(var(--body-height, 0px) - 10em);
        }
    }

    &__footer {
        width: 100%;
        padding: 0 16px 16px 16px;
        z-index: 9999;

        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;

        background-color: transparent;
        max-width: 880px;
        margin: auto;
    }

    &__loading {
        width: 100%;
        padding: 8px 0;
        margin: 16px 0;
        display: flex;
        justify-content: start;
        align-items: center;
    }

    &__thinking {
        display: flex;
        align-items: center;
        gap: 12px;

        &_message {
            color: var(--palette-gray-90);
            font-weight: 400;
            font-size: 16px;
            line-height: 28px;

            mask-image: linear-gradient(
                90deg,
                var(--palette-gray-100),
                var(--palette-gray-100) 30%,
                rgba(var(--raw-palette-gray-100), 0.4) 50%,
                var(--palette-gray-100) 70%,
                var(--palette-gray-100) 100%
            );
            mask-size: 400% 100%;
            animation: think 3s infinite reverse;
        }
    }

    &__loading-guide-chat {
        display: flex;
        justify-content: flex-start;
        padding: 8px 0;
    }

    &__error {
        width: 100%;
        padding: 8px 16px;
        margin: 16px 0;
    }

    &__empty,
    &__loading-text {
        text-align: center;
        font-size: 14px;
        color: var(--palette-gray-80);
    }
    &__feedback-row {
        display: flex;
        margin-bottom: 0;
        margin-top: 8px;
    }

    &__copied-message {
        color: var(--palette-gray-70);
        font-size: 12px;
        line-height: 7px;
        margin-left: 8px;
    }

    &__scroll-to-bottom {
        border-radius: 50%;
        position: relative;
        bottom: 20px;
        z-index: 1000;
        box-shadow: 0 4px 4px 0 var(--semantic-drop-shadow-base);
        min-width: 32px;
    }
}

// guides chat specific styling

.ask-pendo-thread__guide-chat {
    padding: 0;

    .ask-pendo-thread__messages-container {
        padding: 24px 16px 0;
    }
}

@keyframes think {
    100% {
        mask-position: right;
    }
}
</style>
